import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Info, Scale } from "lucide-react";
import { useTenant } from "@/context/TenantContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import api from "@/lib/api";
import ScenariosSection from "@/components/improvement/ScenariosSection";

// Macro-Area A: il Simulatore legge SOLO da ScoreRun (score-runs/current).
const DISPLAY_STATUS = {
  CURRENT: { label: "Aggiornato", cls: "bg-green-100 text-green-800" },
  STALE: { label: "Da ricalcolare", cls: "bg-amber-100 text-amber-800" },
  INSUFFICIENT_DATA: { label: "Dati insufficienti", cls: "bg-red-100 text-red-700" },
  CALCULATING: { label: "Calcolo in corso…", cls: "bg-sky-100 text-sky-800" },
  FAILED: { label: "Calcolo non riuscito", cls: "bg-red-100 text-red-700" },
};
const DIM_LABELS = { E: "Environmental", S: "Social", G: "Governance" };
const DIM_FIELDS = { E: "environmental_score", S: "social_score", G: "governance_score" };

export default function Simulator() {
  const { companyId, period } = useTenant();
  const [current, setCurrent] = useState(undefined);

  useEffect(() => {
    if (!companyId || !period?.id) return;
    api.get(`/companies/${companyId}/periods/${period.id}/score-runs/current`)
      .then((r) => setCurrent(r.data))
      .catch(() => setCurrent(null));
  }, [companyId, period?.id]);

  const run = current?.run;
  const disp = run?.display_status ? DISPLAY_STATUS[run.display_status] : null;
  const insufficient = run?.display_status === "INSUFFICIENT_DATA";

  return (
    <div className="space-y-6" data-testid="simulator-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">Simulatore ESG Score</h1>
          <p className="text-sm text-slate-600 mt-1">
            Ipotesi what-if sul periodo <strong>Esercizio {period?.year}</strong>: la simulazione non modifica alcun dato.
          </p>
        </div>
        <Link to="/piano-miglioramento"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-green-800 bg-white border border-green-300 hover:bg-green-100 rounded-md px-4 py-2 transition-colors"
          data-testid="goto-improvement-plan">
          Piano di miglioramento <ArrowRight className="w-4 h-4" aria-hidden="true" />
        </Link>
      </div>

      <Card className="border-slate-200" data-testid="sim-current-score-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Scale className="w-4 h-4 text-green-700" aria-hidden="true" />
            Score attuale (base delle simulazioni)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {current === undefined ? (
            <div className="flex items-end gap-6 animate-pulse" data-testid="sim-score-loading" aria-busy="true">
              <div className="h-10 w-20 rounded bg-slate-200" />
              <div className="h-6 w-40 rounded bg-slate-100" />
            </div>
          ) : !run ? (
            <div className="space-y-2" data-testid="sim-score-empty">
              <p className="text-sm text-slate-600">
                Nessuna valutazione calcolata per questo esercizio: le simulazioni useranno come base
                il prossimo ScoreRun calcolato.
              </p>
              <Link to="/valutazione"
                className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 hover:underline"
                data-testid="sim-goto-valutazione">
                Apri la Valutazione ESG <ArrowRight className="w-3 h-3" aria-hidden="true" />
              </Link>
            </div>
          ) : (
            <div className="space-y-3" data-testid="sim-score-content">
              <div className="flex flex-wrap items-end gap-6">
                <div>
                  <p className="text-xs text-slate-500">ESG PMI Score</p>
                  <p className="text-4xl font-extrabold text-slate-900" data-testid="sim-overall-current">
                    {insufficient ? "—" : run.overall_score ?? "—"}
                  </p>
                </div>
                {disp && (
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded mb-2 ${disp.cls}`}
                    data-testid="sim-score-status">
                    {disp.label}
                  </span>
                )}
                <div className="flex gap-4 mb-1">
                  {["E", "S", "G"].map((d) => (
                    <div key={d} className="text-center" data-testid={`sim-pillar-${d.toLowerCase()}`}>
                      <p className="text-[11px] text-slate-500">{DIM_LABELS[d]}</p>
                      <p className="text-sm font-bold text-slate-800">{insufficient ? "—" : run[DIM_FIELDS[d]] ?? "—"}</p>
                    </div>
                  ))}
                </div>
              </div>
              {run.display_status === "STALE" && (
                <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1"
                  data-testid="sim-score-stale-note">
                  I dati sono cambiati dopo questo calcolo: lo score è da ricalcolare.
                  Le simulazioni fanno riferimento all'ultimo ScoreRun disponibile.
                </p>
              )}
              {insufficient && (
                <p className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1"
                  data-testid="sim-score-insufficient-note">
                  Copertura dati al {run.coverage_percentage ?? 0}%: insufficiente per una valutazione affidabile.
                  Le simulazioni restano disponibili a scopo esplorativo.
                </p>
              )}
              <p className="text-[11px] text-slate-500 flex items-start gap-1.5">
                <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden="true" />
                Metodologia {run.methodology_version} · calcolato il{" "}
                {new Date(run.calculated_at).toLocaleDateString("it-IT")} · stato {run.status}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Fase 10: scenari what-if riusano lo Score Engine Fase 9 (ScoreRun come base). */}
      <ScenariosSection companyId={companyId} period={period} />
    </div>
  );
}
